import React, { useEffect } from 'react';
import { Navigate, useLocation, useRouteError } from 'react-router';
import { Box, Button, Typography } from '@mui/material';

/**
 * Router-level `errorElement`.
 *
 * Without this, react-router renders its built-in "Unexpected Application
 * Error!" screen — raw stack trace and a note addressed to developers, which is
 * not something an end user should ever see. Anything that throws while
 * resolving or rendering a route sends the user home instead.
 *
 * The cause is still reported to the console, so this hides the screen, not the
 * bug.
 */

export const HOME_PATH = '/dashboards';

const REDIRECT_KEY = 'routeErrorRedirectAt';
const REDIRECT_COOLDOWN_MS = 15000;

/**
 * Guards against a redirect ping-pong. If the broken route is one the app
 * bounces back to (e.g. a missing login chunk: /auth/login -> home ->
 * ProtectedRoute -> /auth/login), redirecting again would loop forever, so the
 * second failure inside the cooldown renders a message instead.
 */
const mayRedirect = () => {
  try {
    const last = Number(sessionStorage.getItem(REDIRECT_KEY)) || 0;
    if (Date.now() - last < REDIRECT_COOLDOWN_MS) return false;
    sessionStorage.setItem(REDIRECT_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
};

const RouteErrorFallback = () => {
  const error = useRouteError();
  const { pathname } = useLocation();

  useEffect(() => {
    console.error('[RouteErrorFallback] route failed:', pathname, error);
  }, [pathname, error]);

  // Redirecting to the page that just failed would bounce forever, so when the
  // home route itself is the broken one we show a quiet message instead.
  if (pathname === HOME_PATH || !mayRedirect()) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          minHeight: '60vh',
          textAlign: 'center',
          px: 3,
        }}
      >
        <Typography variant="h5">This page failed to load</Typography>
        <Typography variant="body2" color="textSecondary">
          Please reload the page. If it keeps failing, contact your administrator.
        </Typography>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Reload
        </Button>
      </Box>
    );
  }

  return <Navigate to={HOME_PATH} replace />;
};

export default RouteErrorFallback;
